import { Globe } from 'lucide-react';
import type { RegionSlug } from '../../lib/apiClient';
import { useMasterData } from '../../context/MasterDataContext';

interface RegionSelectProps {
  value: RegionSlug | '';
  onChange: (value: RegionSlug) => void;
  label?: string;
}

/**
 * Region picker for candidate signup.
 *
 * Reads from `MasterDataContext` so the region list comes from a single cached fetch shared
 * across the whole app. The four-region fallback below covers two cases:
 *   1. The master-data fetch is still in flight (signup page rendered before it resolved).
 *   2. The fetch failed (offline / backend down) — signup must still be possible because the
 *      backend Zod schema also accepts these slugs.
 * The fallback list intentionally matches the v1 backend seed; expanding regions is an
 * Admin-App / Phase 8 concern that will require this fallback to come out.
 */
const FALLBACK_REGIONS: { slug: RegionSlug; name: string }[] = [
  { slug: 'usa', name: 'United States' },
  { slug: 'canada', name: 'Canada' },
  { slug: 'india', name: 'India' },
  { slug: 'thailand', name: 'Thailand' },
];

export function RegionSelect({ value, onChange, label = 'Region' }: RegionSelectProps) {
  const { data, status } = useMasterData();
  const fromCtx = data?.regions ?? [];
  const options = fromCtx.length > 0
    ? fromCtx.map((r) => ({ slug: r.slug as RegionSlug, name: r.name }))
    : FALLBACK_REGIONS;
  const isLoading = status === 'loading' && fromCtx.length === 0;

  return (
    <div className="space-y-2">
      <label className="text-[11px] font-black uppercase tracking-widest text-brand-muted block">
        {label}
      </label>
      <div className="relative">
        <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
        <select
          required
          value={value}
          onChange={(e) => onChange(e.target.value as RegionSlug)}
          className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none transition-all font-medium appearance-none cursor-pointer"
        >
          <option value="" disabled>
            {isLoading ? 'Loading regions…' : 'Select your region…'}
          </option>
          {options.map((r) => (
            <option key={r.slug} value={r.slug}>
              {r.name}
            </option>
          ))}
        </select>
      </div>
      <p className="text-xs text-brand-muted/70">
        Region is locked once you sign up — applications are scoped to your region.
      </p>
    </div>
  );
}
